// src/components/feature/FavoritesProvider.tsx
//
// React binding for the per-account favourites store
// (src/lib/favorites.ts).
//
// Two rules live here:
//   1. ANONYMOUS VISITORS GET NO WRITE TOGGLE. The provider only
//      loads the list for a signed-in account; the toggle component
//      renders nothing at all when there is no session.
//   2. ACCOUNT SWITCHING IS SAFE. The store is keyed by the signed-in
//      subject: when the subject changes (or the session ends), the
//      previous account's list is dropped BEFORE the new one loads, so
//      the new account never renders the old account's favourites.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useAuth } from "@/components/auth/AuthContext";
import {
  getSnapshot,
  loadFavorites,
  resetFavorites,
  subscribe,
  toggleFavorite,
  type FavoritesSnapshot,
} from "@/lib/favorites";

export interface FavoritesContextValue extends FavoritesSnapshot {
  /** True only for a signed-in account — anonymous visitors get no
   *  write toggle at all. */
  canWrite: boolean;
  /** True while this product's write is in flight. */
  isPending: (productId: number) => boolean;
  /** True inside the post-write cooldown: the button refuses a click. */
  isLocked: (productId: number) => boolean;
  /** True when the product is in the signed-in account's list. */
  isFavorite: (productId: number) => boolean;
  /** Toggle a favourite. Resolves to the new state, or null on error. */
  toggle: (productId: number) => Promise<boolean | null>;
  /** Re-read the list from the server. */
  reload: () => Promise<void>;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // The signed-in subject: the account the list belongs to.
  const subject = auth.isAuthenticated ? (auth.user?.sub ?? "") : "";
  const issuer = auth.user?.issuer ?? "";

  useEffect(() => {
    if (!auth.isReady) return;
    if (!subject) {
      // Signed out: forget everything the previous account had.
      resetFavorites();
      return;
    }
    // A (re)load always starts from an empty list so the previous
    // account's products are never rendered under the new account.
    resetFavorites();
    void loadFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isReady, subject, issuer]);

  const isFavorite = useCallback(
    (productId: number) => snapshot.ids.includes(productId),
    [snapshot.ids],
  );
  const isLocked = useCallback(
    (productId: number) => snapshot.locked.includes(productId),
    [snapshot],
  );
  const isPending = useCallback(
    (productId: number) => snapshot.pending.includes(productId),
    [snapshot.pending],
  );

  const value = useMemo<FavoritesContextValue>(
    () => ({
      ...snapshot,
      canWrite: Boolean(subject),
      isFavorite,
      isPending,
      isLocked,
      toggle: toggleFavorite,
      reload: loadFavorites,
    }),
    [snapshot, subject, isFavorite, isPending, isLocked],
  );

  return (
    <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>
  );
}

/**
 * The signed-in account's favourites. Outside a FavoritesProvider this
 * returns an inert, read-only value: no toggle may be rendered, which
 * is exactly the anonymous-visitor behaviour.
 */
export function useFavorites(): FavoritesContextValue {
  const ctx = useContext(FavoritesContext);
  return (
    ctx ?? {
      ids: [],
      products: [],
      loading: false,
      error: null,
      pending: [],
      locked: [],
      canWrite: false,
      isPending: () => false,
      isLocked: () => false,
      isFavorite: () => false,
      toggle: async () => null,
      reload: async () => undefined,
    }
  );
}
